"use client";

import { subscribeToRecipes } from "@/lib/recipe-service";
import type { Recipe } from "@/lib/types";
import type { FirestoreError } from "firebase/firestore";
import { useEffect, useState } from "react";
import type { RecipeBox } from "@/lib/types";

type AllRecipesState = {
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
};

/**
 * Hook to load recipes from multiple recipe boxes
 */
export function useAllRecipes(boxes: RecipeBox[] | null | undefined): AllRecipesState {
  const [state, setState] = useState<AllRecipesState>({ recipes: [], loading: true, error: null });

  useEffect(() => {
    if (!boxes || boxes.length === 0) {
      setState({ recipes: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribes: (() => void)[] = [];

    const recipeMap = new Map<string, Recipe>(); // Use Map to deduplicate by ID

    let loadedCount = 0;
    const totalBoxes = boxes.length;

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount === totalBoxes) {
        setState({ recipes: Array.from(recipeMap.values()), loading: false, error: null });
      }
    };

    boxes.forEach((box) => {
      const unsubscribe = subscribeToRecipes(
        box.id,
        (recipes) => {
          // Update recipes for this box
          recipes.forEach((recipe) => {
            recipeMap.set(recipe.id, recipe);
          });
          
          // Remove recipes that are no longer in this box
          const currentBoxRecipeIds = new Set(recipes.map((r) => r.id));
          Array.from(recipeMap.entries()).forEach(([id, recipe]) => {
            if (recipe.recipeBoxId === box.id && !currentBoxRecipeIds.has(id)) {
              recipeMap.delete(id);
            }
          });

          setState({ recipes: Array.from(recipeMap.values()), loading: false, error: null });
        },
        (error: FirestoreError) => {
          console.error(`Failed to load recipes from box ${box.id}`, error);
          checkComplete();
        },
      );
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [boxes]);

  return state;
}

